import { Injectable, Injector, inject, type ViewContainerRef } from '@angular/core';
import { BehaviorSubject, firstValueFrom, take } from 'rxjs';
import { HlmDialogOpener } from '@lite-workspace/shared/helm-ui/dialog';
import { ToastService } from '@lite-workspace/shared/helm-ui/toast';
import { PredefinedFileTags } from '@lite-workspace/dto-fotoclass-photo';
import type { UploadBucket } from '../../../../core/photo-upload-api.service';
import {
  type ImageSize,
  type IntakeConflict,
  type IntakeLimitHold,
  type IntakePlan,
  type IntakeSummary,
  PhotoUploadCoordinatorService,
  groupFrameIds,
  intakeFileKey,
  normalizedNameKey,
} from './photo-upload-coordinator.service';
import { ProjectsStore } from '../+state/projects.store';
import type { Photo } from '../entities/project.models';
import { intakeSummaryMessage, replacedTitle } from './intake-summary';
import { stripEditSuffix } from '../../ui/retouch-pairing';
import { UploadIntakeFlagService, type NameSource } from './upload-intake-flag.service';
import {
  IntakeCheckFailedDialogComponent,
  type IntakeCheckFailedDecision,
  type IntakeCheckFailedDialogContext,
} from '../../ui/intake-check-failed-dialog.component';
import {
  type MoveDecision,
  type PortraitStep,
  type PreviewFile,
  ReplaceConfirmDialogComponent,
  type ReplaceConfirmDialogContext,
  type ReplaceConfirmResult,
  type WizardReplaceDecision,
} from '@lite-workspace/shared/blocks/replace-confirm-dialog';
import {
  RejectedUploadsDialogComponent,
  type RejectedUploadsDialogContext,
  rejectedUploadsContext,
} from '@lite-workspace/uploader-core';
import {
  AmbiguousReplaceDialogComponent,
  type AmbiguousReplaceDecision,
  type AmbiguousReplaceDialogContext,
} from '../../ui/ambiguous-replace-dialog.component';

export interface IntakeOutcome {
  readonly committed: boolean;
  readonly summary: IntakeSummary | null;
  /** Tagged commits only: resolves once their rewrites settle — old row id → the new photo that landed. */
  readonly replacedPairs?: Promise<readonly { readonly oldId: string; readonly photo: Photo }[]>;
  /** Retouch intent only: names matching no project photo — refused, never uploaded as new. */
  readonly refusedUnknown?: readonly string[];
  /** `summaryBatch` runs only: the server batch the summary is read by. */
  readonly uploadBatchId?: string;
}

export interface RetouchIntakeAssessment {
  /** Name matches a project photo in any state — will be uploaded. */
  matched: File[];
  /** Names among `matched` whose photo is used in no album. */
  notInAlbums: string[];
  /** Names matching nothing in the project — refused, never uploaded. */
  unknown: string[];
}

const CANCELLED: IntakeOutcome = { committed: false, summary: null };

/** The wizard's project may not exist yet (ensureProject in flight) — its reservations share one key. */
const PENDING_PROJECT_KEY = '<pending>';

/** A name claimed by a paused intake. A later drop of the same name swaps its newer file in here
 *  ('replace' — latest file wins) instead of racing a second upload; the claim holder commits the slot. */
interface ReservationSlot {
  file: File;
  /** Dimensions measured for a SWAPPED-IN file by the assessment that accepted it. The holder measured
   *  a different File object, so without this the commit would enqueue the newcomer with no size. */
  size: ImageSize | null;
}

/** Files THIS assessment let through the client gates — type, weight and resolution all answered.
 *  `rejected` names are deliberately absent: they may not ride into a paused intake's slot. */
function validatedFiles(plan: IntakePlan): ReadonlySet<File> {
  return new Set<File>([
    ...plan.candidates,
    ...plan.conflicts.map(conflict => conflict.file),
    ...plan.ambiguous.map(entry => entry.file),
  ]);
}

/** Замены, чью цель клиент нашёл по второму написанию того же JPEG-имени: окно называет оба. */
function renamedPairs(conflicts: readonly IntakeConflict[]): { readonly from: string; readonly to: string }[] {
  return conflicts
    .filter(conflict => conflict.storedName !== undefined)
    .map(conflict => ({ from: conflict.file.name, to: conflict.storedName as string }));
}

function ambiguityContext(plan: IntakePlan): AmbiguousReplaceDialogContext {
  return {
    // Переписываются строки под ХРАНИМЫМ написанием: одно имя дропа назвало бы кадр, которого в проекте нет.
    names: plan.ambiguous.map(entry =>
      entry.storedName === undefined ? entry.file.name : `${entry.file.name} → ${entry.storedName}`,
    ),
    matches: Math.max(...plan.ambiguous.map(entry => entry.matches)),
    // The scale of «заменить все», in full: the window may not hide behind «и ещё N» how many photos
    // one click rewrites (FKS-11259).
    rows: plan.ambiguous.reduce((total, entry) => total + entry.matches, 0),
    // Offered only when EVERY listed name has a row on the page — a partial aim would leave the person
    // guessing which of the names that button covers.
    aimed: plan.ambiguous.every(entry => entry.preferredId !== null),
  };
}

const byCodeUnit = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);

/** What the person actually decided on: the check verdict and WHICH names replace / go as new / are held —
 *  the unaimable ones with the width the window promised for each. Two plans with one key ask for the same
 *  consent, so an unchanged key commits the FRESH plan; a changed one goes back and asks again. */
function planDecisionKey(plan: IntakePlan): string {
  const names = (files: readonly File[]): string =>
    files
      .map(file => normalizedNameKey(file.name))
      .sort(byCodeUnit)
      .join('|');
  return [
    plan.check,
    // Хранимое написание — часть решения: замена под чужим расширением спрашивается отдельно.
    plan.conflicts
      .map(conflict => `${normalizedNameKey(conflict.file.name)}>${normalizedNameKey(conflict.storedName ?? '')}`)
      .sort(byCodeUnit)
      .join('|'),
    plan.ambiguous
      .map(entry => {
        const name = `${normalizedNameKey(entry.file.name)}>${normalizedNameKey(entry.storedName ?? '')}`;
        return `${name}:${entry.matches}:${entry.preferredId ?? ''}`;
      })
      .sort(byCodeUnit)
      .join('|'),
    names([...plan.unverified]),
  ].join('#');
}

/** The wizard's two portrait steps. «Все остальные фото» never takes part in a move — recognition owns
 *  what lands there, and a re-drop onto a portrait step must not pull it out (техспека § Решения). */
function isPortraitStep(bucket: UploadBucket): bucket is PortraitStep {
  return bucket === 'student' || bucket === 'teacher';
}

/** There are exactly two portrait steps, so the source of a move is whichever one was not dropped on. */
function otherStep(step: PortraitStep): PortraitStep {
  return step === 'student' ? 'teacher' : 'student';
}

function conflictNameKeys(conflicts: readonly IntakeConflict[]): ReadonlySet<string> {
  return new Set(conflicts.map(conflict => normalizedNameKey(conflict.file.name)));
}

/** The commit shape of an answered ambiguity window. */
function ambiguousReplaceMode(decision: AmbiguousReplaceDecision | null): 'aimed' | 'all' | undefined {
  if (decision === 'replace-all') return 'all';
  if (decision === 'replace-aimed') return 'aimed';
  return undefined;
}

export interface IntakeRunOptions {
  /** Rows the caller knows are on the page — the editor passes the photos standing in the open album,
   *  which is what lets a repeated name be aimed at the ONE photo the person actually sees. */
  readonly preferredTargetIds?: readonly string[];
  /** «Имена из названий файлов» — ответ переключателя в окне загрузки. Метка ставится на ВОЛНУ при
   *  её рождении; сервер сам разбирает имена, когда волна дообработается. */
  readonly nameSource?: NameSource;
  /** Upload from the retouch section: no window, every rewrite tagged `retouched`; the caller counts what
   *  landed, the flow names only the refusals. A repeated name rewrites the `preferredTargetIds` row,
   *  one aiming none is left. */
  readonly intent?: 'retouch';
  /** Photos page with retouch on: the replace window opens on any replace whatever the flag says and
   *  carries the «Это ретушь» switch; its answer on = rewrites tagged `retouched`. */
  readonly retouchSwitch?: boolean;
  /** Photos page: every replace asks whatever the flag says, in the window's photos variant — «Перезаписать»,
   *  and on a mixed drop «Загрузить новые M фото», which skips the matched names. */
  readonly replaceVariant?: 'photos';
  /** One dropped file rewrites THIS photo whatever its own name, under the stored name. */
  readonly targetId?: string;
  /** Bucket the drop's new photos land in — the Photos page passes its open chip. Defaults to `other`;
   *  the retouch intent ignores it, the section only rewrites. */
  readonly bucket?: UploadBucket;
  /** Photos page staff upload: the drop is one server batch with its tab, read back as the upload summary. */
  readonly summaryBatch?: { readonly bucket: UploadBucket };
}

/**
 * The one pre-upload pause of a ready project — both surfaces (Photos page, album editor) enter here:
 * assess (prepareIntake) → decide → commit. The runtime flag holds the check-failed stop and the replace
 * confirmation of a target the project holds under the DROP'S OWN name: flag OFF keeps today's path for
 * those two (such a row replaces silently, an unanswered check rejects fail-closed). A replace whose target
 * the CLIENT found by the other JPEG spelling asks with the flag OFF too — the person never saw that name,
 * and no rewrite may land on a name nobody was shown. The ambiguity window is NOT flagged —
 * a name the project holds several times has no silent answer that is safe (FKS-11259), so it is always
 * asked and the flag is no longer a full rollback of this flow. No file uploads before it all resolves.
 */
@Injectable({ providedIn: 'root' })
export class UploadIntakeFlowService {
  private readonly coordinator = inject(PhotoUploadCoordinatorService);
  private readonly flag = inject(UploadIntakeFlagService);
  private readonly dialogs = inject(HlmDialogOpener);
  private readonly toast = inject(ToastService);
  /** The store is reached lazily — only a step move needs it, so no surface pays for it on a plain drop. */
  private readonly injector = inject(Injector);

  /** Names held by wizard intakes still paused before commit, per project. The coordinator only sees
   *  files at commit — without this claim two overlapping drops of one name both look conflict-free. */
  private readonly wizardReservations = new Map<string, Map<string, ReservationSlot>>();

  /** Дропы, которые сейчас держат вопрос: один дроп спрашивает несколько окон подряд. */
  private askingDrops = 0;
  private readonly askingSubject = new BehaviorSubject(false);
  /** true, пока хоть один дроп ждёт ответа — итог прошлой перезаписи не ложится между его окнами. */
  readonly asking$ = this.askingSubject.asObservable();

  /** The drop's number stays held against the project cap from its check until it commits or is cancelled. */
  async run(
    vcr: ViewContainerRef,
    projectId: string | null,
    files: readonly File[],
    options?: IntakeRunOptions,
  ): Promise<IntakeOutcome> {
    const limitHold: IntakeLimitHold = { released: false };
    try {
      return await this.runHeld(vcr, projectId, files, limitHold, options);
    } finally {
      limitHold.released = true;
    }
  }

  private async runHeld(
    vcr: ViewContainerRef,
    projectId: string | null,
    files: readonly File[],
    limitHold: IntakeLimitHold,
    options?: IntakeRunOptions,
  ): Promise<IntakeOutcome> {
    if (files.length === 0) return CANCELLED;
    const retouchIntent = options?.intent === 'retouch';
    const retouchSwitch = options?.retouchSwitch === true;
    const photosVariant = options?.replaceVariant === 'photos';
    const targetId = options?.targetId;
    const bucket: UploadBucket = retouchIntent ? 'other' : (options?.bucket ?? 'other');
    // The retouch section asks nothing: a failed check keeps the unverified files back, fail-closed.
    const guarded = retouchIntent ? false : await this.flag.confirmReplaceEnabled();
    /** The «Это ретушь» answer — carried across stale re-asks as the switch's starting position. */
    let retouchOn = false;
    /** «Загрузить новые M фото»: the matched names are skipped, the rest uploads. The last window's answer. */
    let newOnly = false;
    /** Names the person dropped out of THIS drop by closing the ambiguity window. They are filtered out of
     *  every later assessment — otherwise the same question would reopen for ever — and the outcome names
     *  them, so a stray Esc never swallows a batch in silence. */
    const cancelled = new Set<string>();
    const cancelledNames: string[] = [];
    /** Retouch intent: names matching no project photo. Never uploaded as new — the section only rewrites. */
    let refusedUnknown: string[] = [];
    const assess = async (): Promise<IntakePlan> => {
      const assessed = await this.coordinator.prepareIntake(projectId, files, options?.preferredTargetIds, undefined, limitHold);
      const aimed = targetId === undefined ? assessed : await this.coordinator.aimIntakeAt(projectId, assessed, targetId);
      const plan = this.withoutKeys(aimed, cancelled);
      if (!retouchIntent) return plan;
      refusedUnknown = plan.candidates.map(file => file.name);
      return { ...plan, candidates: [] };
    };

    let plan = await assess();
    let unverifiedAsNew = false;
    let ambiguity: AmbiguousReplaceDecision | null = null;
    /** Names answered «загрузить как новое фото» — carried into the commit so a re-assessment that turns
     *  one of them into a single-row conflict cannot invert that answer into a replacement. */
    const uploadAsNewNames = new Set<string>();

    for (;;) {
      /** Did a window hold the flow this round? Only then can the plan have gone stale under it. */
      let asked = false;

      if (guarded) {
        while (plan.check === 'failed') {
          const answer = await this.askCheckFailed(vcr);
          if (answer === undefined) return CANCELLED;
          if (answer === 'as-new') {
            // A promise about NAMES — it has to survive the staleness re-check below, like any other.
            unverifiedAsNew = true;
            asked = true;
            break;
          }

          // «Повторить» re-assesses right here, so nothing it produced can already be stale.
          plan = await assess();
        }
      }

      // The ambiguity pause is NOT behind the flag. With it off a name the project holds several times used
      // to upload as one more copy with nothing said, and the person had no way out inside the product
      // (FKS-11259) — so this question is asked whatever the flag says.
      if (plan.ambiguous.length > 0 && retouchIntent) {
        // The section named its rows: a repeated name rewrites the one in scope; a name aiming none — or
        // several — is left alone and named as skipped, never guessed.
        for (const entry of plan.ambiguous) {
          if (entry.preferredId !== null) continue;
          const key = normalizedNameKey(entry.file.name);
          if (!cancelled.has(key)) cancelledNames.push(entry.file.name);
          cancelled.add(key);
        }

        plan = this.withoutKeys(plan, cancelled);
        ambiguity = 'replace-aimed';
      } else if (plan.ambiguous.length > 0) {
        const asking = plan.ambiguous.map(entry => entry.file);
        const decision = await this.askAmbiguousReplace(vcr, ambiguityContext(plan));
        asked = true;
        ambiguity = decision ?? null;
        for (const file of asking) {
          const key = normalizedNameKey(file.name);
          if (decision === undefined) {
            // Esc, the backdrop or «Пропустить» answer for THESE names only: the unambiguous and the
            // brand-new files of the same drop keep going, and the toast says what was left behind.
            if (!cancelled.has(key)) cancelledNames.push(file.name);
            cancelled.add(key);
          } else if (decision === 'as-new') uploadAsNewNames.add(key);
          else uploadAsNewNames.delete(key);
        }

        if (decision === undefined) plan = this.withoutKeys(plan, cancelled);
      }

      // Threshold: one replace. Names the intake could not aim have their own always-on window above —
      // mentioning them here too would ask about one drop twice; a name already answered «загрузить как
      // новое» is not up for replacement either, so counting it here would promise the wrong thing.
      const replacing = plan.conflicts.filter(conflict => !uploadAsNewNames.has(normalizedNameKey(conflict.file.name)));
      const replaceCount = replacing.length;
      // Answered «загрузить как новое» — these upload too, so «Остальные M» counts them.
      const asNewCount = [...plan.conflicts.map(conflict => conflict.file), ...plan.ambiguous.map(entry => entry.file)].filter(
        file => uploadAsNewNames.has(normalizedNameKey(file.name)),
      ).length;
      // Имя цели подобрал КЛИЕНТ (перезалив `.jpeg` поверх хранимого `.jpg`), а не человек — такая замена
      // не может примениться молча даже при выключенном флаге, и окно обязано назвать оба написания.
      const renamed = renamedPairs(replacing);
      // The retouch switch lives only in this window, so with it the window opens whatever the flag says;
      // the Photos page always asks.
      if (!retouchIntent && (guarded || renamed.length > 0 || retouchSwitch || photosVariant) && replaceCount > 0) {
        const answer = await this.askReplaceConfirm(vcr, {
          replaceCount,
          ambiguousCount: 0,
          // M > N switches the body to «Остальные M−N загрузятся как новые».
          total: plan.candidates.length + replaceCount + asNewCount,
          renamed,
          ...(retouchSwitch ? { retouch: retouchOn } : {}),
          ...(photosVariant ? { variant: 'photos' as const } : {}),
        });
        // «Отмена» sends nothing — the new files included.
        if (!answer.confirmed) return CANCELLED;
        retouchOn = answer.retouch ?? retouchOn;
        newOnly = answer.newOnly === true;
        asked = true;
      }

      // No window was opened → nothing could go stale under it; also the as-new consent stands.
      if (!asked) break;

      // The dialogs held the flow while the project kept living — never commit a stale promise:
      // re-assess, and if the decision itself changed (new conflicts, a name that stopped being
      // unaimable, a different check verdict), go back and ask again; an unchanged decision commits
      // the FRESH plan (current target ids).
      const fresh = await assess();
      const unchanged = planDecisionKey(fresh) === planDecisionKey(plan);
      plan = fresh;
      if (unchanged) break;
      unverifiedAsNew = false;
      ambiguity = null;
    }

    // The bucket is the only source of a new person's role: `student`/`teacher` tag the upload with it;
    // `other` tags nothing, so a content portrait the server still turns into a person (its own flag) gets
    // the default role. A replacement lands in the bucket of the row it rewrites, `other` when that is unknown.
    const summary = await this.coordinator.commitIntake(projectId, bucket, plan, {
      unverifiedAsNew,
      replaceAmbiguous: ambiguousReplaceMode(ambiguity),
      uploadAsNewNames: [...uploadAsNewNames],
      nameSource: options?.nameSource,
      unknownTargetBucket: 'other',
      ...(retouchIntent || retouchOn ? { replaceFileTags: [PredefinedFileTags.RETOUCHED] } : {}),
      ...(newOnly ? { skipConflicts: true } : {}),
      ...(options?.summaryBatch ? { summaryBatch: options.summaryBatch } : {}),
    });
    // A retouch name left out matched several project photos, none in scope — «имя уже в проекте» would lie.
    const reported: IntakeSummary =
      cancelledNames.length === 0
        ? summary
        : retouchIntent
          ? { ...summary, skippedAmbiguous: [...(summary.skippedAmbiguous ?? []), ...cancelledNames] }
          : { ...summary, skipped: [...summary.skipped, ...cancelledNames] };
    // The retouch section counts what landed itself; the flow still names every refusal. The Photos page names
    // its rewrites only once they land (below), and the names «Загрузить новые» skipped not at all.
    this.reportOutcome(
      vcr,
      retouchIntent
        ? { ...reported, queued: 0, replaced: 0 }
        : photosVariant
          ? { ...reported, replaced: 0, skipped: cancelledNames }
          : reported,
    );
    const replacements = summary.replacements;
    const landed = replacements ? this.coordinator.landedReplacements(replacements) : undefined;
    // Landed bytes only: a failed or cancelled rewrite is not counted, none landed = no toast.
    if (photosVariant && landed)
      void landed.then(pairs => {
        if (pairs.length > 0) this.toast.show(replacedTitle(pairs.length), 'success');
      });
    return {
      committed: true,
      summary: reported,
      // Tagged rewrites only: the host patches the rows it tagged.
      ...(landed && (retouchIntent || retouchOn) ? { replacedPairs: landed } : {}),
      ...(retouchIntent ? { refusedUnknown } : {}),
      ...(options?.summaryBatch && summary.uploadBatchId ? { uploadBatchId: summary.uploadBatchId } : {}),
    };
  }

  /** Retouch drop, before any upload: each file's name — own, or with Lightroom's `-Edit` cut — against ALL
   *  project photos, whatever their retouch state. Unknown names are refused; a name the check could not
   *  answer counts as matched — the intake then holds it back fail-closed, never uploads it as new. */
  async assessRetouchIntake(projectId: string, files: File[]): Promise<RetouchIntakeAssessment> {
    const probes = new Map<File, File[]>();
    for (const file of files) {
      const stripped = stripEditSuffix(file.name);
      probes.set(file, stripped === null ? [file] : [file, new File([], stripped, { type: file.type })]);
    }

    const rows = await this.coordinator.projectRowsByName(projectId, [...probes.values()].flat());
    const matched: File[] = [];
    const unknown: string[] = [];
    const idsOf = new Map<File, readonly string[] | null>();
    for (const [file, own] of probes) {
      const hit = own.find(probe => rows.has(probe));
      if (hit === undefined) unknown.push(file.name);
      else {
        matched.push(file);
        idsOf.set(file, rows.get(hit) ?? null);
      }
    }

    const known = [...idsOf.values()].some(ids => ids !== null && ids.length > 0);
    const occupied = known ? await this.coordinator.occupiedPhotoIds(projectId) : null;
    // Unknown usage (partial scan, failed read, unanswered name) is never reported as «in no album».
    const notInAlbums = occupied
      ? matched.filter(file => {
          const ids = idsOf.get(file);
          return !!ids && ids.length > 0 && ids.every(id => !occupied.has(id));
        })
      : [];
    return { matched, notInAlbums: notInAlbums.map(file => file.name), unknown };
  }

  /** The one place a committed batch speaks: the counts as a toast, the refusals as a window.
   *  `showWindow: false` = the caller's surface is already torn down: the toast is root-level and
   *  survives it, but a dialog attaches through the caller's ViewContainerRef and may not. */
  private reportOutcome(vcr: ViewContainerRef, summary: IntakeSummary, showWindow = true): void {
    this.toastSummary(summary);
    // The toast counts refusals and dies in five seconds; WHICH files and what minimum they missed
    // only fits — and only stays — in a window the person closes themselves.
    if (summary.batchMintFailed || !showWindow) return;
    const context = rejectedUploadsContext(summary.rejected);
    if (context) this.showRejected(vcr, context);
  }

  /** strictBatch's lost mint queued nothing — that failure outranks any per-file summary line. */
  private toastSummary(summary: IntakeSummary): void {
    if (summary.batchMintFailed) {
      this.toast.error('Не удалось начать загрузку — проверьте соединение и повторите');
      return;
    }

    const message = intakeSummaryMessage(summary);
    if (message) this.toast.show(message.title, message.tone, undefined, message.detail);
  }

  private showRejected(vcr: ViewContainerRef, context: RejectedUploadsDialogContext): void {
    this.dialogs.open<RejectedUploadsDialogContext, void>(RejectedUploadsDialogComponent, vcr, context);
  }

  /**
   * The wizard's pre-upload pause — always on, no flag. Differences from a ready project's run():
   * matched names ask replace-or-skip (skip still uploads the fresh files), multi-row names are
   * always skipped without a dialog, and the failed check offers no upload-as-new escape (fail closed).
   * Persons seeding is the caller's call: `vetoNewPersons` rides through to the commit unchanged.
   */
  async runWizard(
    vcr: ViewContainerRef,
    projectId: string | null,
    files: readonly File[],
    bucket: UploadBucket,
    options?: {
      readonly vetoNewPersons?: boolean;
      readonly strictBatch?: boolean;
      /** Без портретных шагов: раздел зовётся «Все фото», как в степпере — окна и тост говорят так же. */
      readonly allInOne?: boolean;
      /** The caller is gone — this flow is root-provided and outlives the step, so every await here can
       *  resume on a torn-down wizard. Checked before the commit: nothing may start uploading after that. */
      readonly abandoned?: () => boolean;
      /** Метка волны: чем сервер будет именовать её кадры. Без неё — прежний `filename`. */
      readonly nameSource?: NameSource;
      /** Пути брошенной папки. Пачка с ними ключуется путями НАСКВОЗЬ — включая слоты-брони ниже:
       *  по имени `A/1.jpg` и `B/1.jpg` делили бы одну бронь, и второй кадр подменялся бы первым. */
      readonly pathByFile?: ReadonlyMap<File, string>;
    },
  ): Promise<IntakeOutcome> {
    if (files.length === 0) return CANCELLED;
    const held = this.heldNames(projectId);
    const claimed = new Map<string, ReservationSlot>();
    const keyOf = (file: File): string => intakeFileKey(file, options?.pathByFile);
    const limitHold: IntakeLimitHold = { released: false };
    const assess = (): Promise<IntakePlan> =>
      this.coordinator.prepareIntake(projectId, files, undefined, options?.pathByFile, limitHold);
    // Claim BEFORE the first await: the synchronous receipt is the only moment no other drop can interleave.
    let stolen = this.claimNames(held, claimed, files, keyOf);
    const abandoned = (): boolean => options?.abandoned?.() === true;
    // Фаза вопросов начинается с оценки и кончается последним ответом — итог прошлой перезаписи ждёт её.
    this.askingSubject.next(++this.askingDrops > 0);
    let asking = true;
    const doneAsking = (): void => {
      if (!asking) return;
      asking = false;
      this.askingSubject.next(--this.askingDrops > 0);
    };

    try {
      // `assessed` is the verdict on the WHOLE drop; `plan` is what this intake commits — the same
      // verdict minus the names a paused intake holds (those land through its slot instead).
      let assessed = await assess();
      let plan = assessed;
      let skipConflicts = false;
      /** Names that already live on the OTHER portrait step. Whatever the person answers they never
       *  upload — either their tags move or they are left alone — so both answers take them out of the
       *  committed plan, and «Оставить» counts as no skip at all (s1-ac1.2, s1-ac4.2). */
      let moving: readonly IntakeConflict[] = [];
      let moveKeys: ReadonlySet<string> = new Set<string>();
      /** «Перенести и перезаписать»: the same move, and then new bytes for every moved row (s1-ac1.3). */
      let rewriting = false;
      const portrait: PortraitStep | null = isPortraitStep(bucket) ? bucket : null;
      const allInOne = options?.allInOne === true;
      for (;;) {
        if (abandoned()) return CANCELLED;
        while (assessed.check === 'failed') {
          const answer = await this.askCheckFailed(vcr, { allowAsNew: false });
          if (answer === undefined) return CANCELLED;
          assessed = await assess();
        }

        stolen = this.claimNames(held, claimed, files, keyOf);
        const visible = this.withoutKeys(assessed, stolen, keyOf);
        const movable = this.movableConflicts(projectId, visible.conflicts, bucket);
        // Stolen names are a paused intake's business, never a move: their file has not landed anywhere yet.
        const replaceCount = visible.conflicts.length - movable.length + stolen.size;
        if (movable.length + replaceCount === 0) {
          // Multi-row names skip silently (no choice exists for them) — the toast still names them.
          plan = visible;
          break;
        }

        let moveDecision: MoveDecision = 'keep';
        if (movable.length > 0 && portrait !== null) {
          const answer = await this.askMove(vcr, {
            replaceCount: movable.length,
            // The unaimable names get their line under whichever window is the only one asked — saying
            // it twice would ask about one drop twice.
            ambiguousCount: replaceCount > 0 ? 0 : visible.ambiguous.length,
            variant: 'move',
            move: {
              from: otherStep(portrait),
              to: portrait,
              files: movable.map(conflict => conflict.file),
              freshCount: visible.candidates.length,
            },
          });
          // × and Esc answer «Оставить»: this window has no cancel — leaving the files where they are IS
          // the harmless answer, and the drop's own new files still go (s1-ac1.2).
          moveDecision = answer ?? 'keep';
        }

        if (replaceCount > 0) {
          // Превью — сами брошенные файлы, сгруппированные по разделу, где уже лежит совпавший кадр.
          const previews = this.replacePreviews(projectId, visible.conflicts, movable, bucket);
          const decision = await this.askWizardReplace(vcr, {
            replaceCount,
            ambiguousCount: visible.ambiguous.length,
            variant: 'wizard',
            // M — everything this drop will actually send: the fresh files plus the ones being rewritten.
            // Without it the window reads every drop as «совпало всё», whatever else the person threw in.
            total: visible.candidates.length + replaceCount,
            previews,
            where: { step: bucket },
            ...(allInOne ? { allInOne } : {}),
            // Имя цели подобрал КЛИЕНТ (перезалив `.jpeg` поверх хранимого `.jpg`) — окно называет оба
            // написания. Переносимые файлы сюда не входят: про них спрашивает окно переноса.
            renamed: renamedPairs(visible.conflicts.filter(conflict => !movable.includes(conflict))),
          });
          // × и Esc отвечают за главную кнопку окна — «Загрузить новые N фото»: плашка обещает, что новые
          // файлы дроп заберёт в любом случае. Дроп отменяется, только когда за него не решено ничего:
          // ни новых файлов, ни ответа про перенос — с ответом перенос идёт, а перезапись пропускается.
          if (decision === undefined && visible.candidates.length === 0 && moveDecision === 'keep') return CANCELLED;
          skipConflicts = decision !== 'replace';
        }

        // The dialog held the flow while the project kept living — commit only a fresh, unchanged decision.
        // The move/replace split is part of that decision: a conflict that changed step under the window
        // was answered for by a question nobody was asked.
        const fresh = await assess();
        const freshStolen = this.claimNames(held, claimed, files, keyOf);
        const freshVisible = this.withoutKeys(fresh, freshStolen, keyOf);
        const freshMovable = this.movableConflicts(projectId, freshVisible.conflicts, bucket);
        const decisionKey = (p: IntakePlan, s: ReadonlySet<string>, m: readonly IntakeConflict[]): string =>
          `${planDecisionKey(p)}#${[...s].sort(byCodeUnit).join('|')}#${[...conflictNameKeys(m)].sort(byCodeUnit).join('|')}`;
        const unchanged = decisionKey(freshVisible, freshStolen, freshMovable) === decisionKey(visible, stolen, movable);
        assessed = fresh;
        if (unchanged) {
          stolen = freshStolen;
          plan = freshVisible;
          moving = moveDecision === 'keep' ? [] : freshMovable;
          rewriting = moveDecision === 'rewrite';
          moveKeys = conflictNameKeys(freshMovable);
          break;
        }
      }

      // Ответы кончились: коммит и загрузка уже ни о чём не спросят.
      doneAsking();
      // Stolen names resolve against the paused holder, not the server: 'replace' swaps the newer
      // file into the holder's slot (latest file wins, holder uploads it), 'skip' drops it here.
      // Only a file this assessment validated may enter that slot — the holder's consent covers a
      // name, not arbitrary bytes, and a rejected file would slip past the client gates on it. A
      // rejected one stays in `plan.rejected` and the toast names it there, as any other refusal.
      const validated = validatedFiles(assessed);
      const stolenNames: string[] = [];
      for (const file of files) {
        const key = keyOf(file);
        if (!stolen.has(key) || !validated.has(file)) continue;
        stolenNames.push(file.name);
        if (skipConflicts) continue;
        const slot = held.get(key);
        if (!slot) continue;
        slot.file = file;
        slot.size = assessed.sizes.get(file) ?? null;
      }

      // Last gate before anything starts uploading: the assessment may have resumed on a wizard the
      // user already left, and page teardown only cancels jobs of a project that never existed.
      if (abandoned()) return CANCELLED;
      // The move goes first: its tiles must already stand on this step when a mixed drop's fresh files
      // start arriving next to them. It never blocks the upload — a failed move says so and the rest
      // of the drop still commits.
      let moved: IntakeSummary['moved'];
      /** Rewrites ride the commit below — but only behind a move that actually landed: on a failure the
       *  rows never left the other step, and new bytes aimed at them would overwrite a photo nobody moved.
       *  A retargeted job already carries the dropped copy as its own bytes, so only the landed rows are
       *  named here — sending them again would put a second upload of one name on the step. */
      let rewrites: readonly IntakeConflict[] = [];
      if (portrait !== null && moveKeys.size > 0) {
        // «Оставить» is an answer too: the files stay put, and the drop says so out loud instead of
        // reading as a silent clean queue (s1-ac1.2, s1-ac6.2).
        const outcome =
          moving.length > 0 && projectId !== null
            ? await this.commitMove(projectId, moving, portrait, rewriting, abandoned)
            : { moved: { count: 0, to: portrait, kept: moveKeys.size }, rewrites: [] };
        moved = outcome.moved;
        rewrites = outcome.rewrites;
        if (rewriting && moved !== undefined && moved.count > 0) moved = { ...moved, rewrite: true };
      }

      // Moved names leave the plan whichever way the window was answered — «Перенести и перезаписать»
      // sends their bytes through `rewriteConflicts`, past the replace window's own skip decision.
      const committing = this.throughSlots(this.withoutKeys(plan, moveKeys, keyOf), claimed, keyOf);
      // Перезаписи, которые лягут в другие разделы: тост говорит о них «Перезаписываем», а результат
      // покажет отдельное окно, когда пачка догрузится. «Пропустить дубли» не перезаписывает ничего.
      const away = skipConflicts ? 0 : this.awayOf(this.replacePreviews(projectId, committing.conflicts, [], bucket), bucket);
      const summary = await this.coordinator.commitIntake(projectId, bucket, committing, {
        vetoNewPersons: options?.vetoNewPersons,
        strictBatch: options?.strictBatch,
        skipConflicts,
        skipAmbiguous: true,
        nameSource: options?.nameSource,
        pathByFile: this.pathsThroughSlots(claimed, keyOf, options?.pathByFile),
        rewriteConflicts: rewrites,
        ...(allInOne ? { allInOne } : {}),
      });
      const counted: IntakeSummary = skipConflicts
        ? { ...summary, skipped: [...summary.skipped, ...stolenNames] }
        : { ...summary, replaced: summary.replaced + stolenNames.length };
      // Шаг мастера — то, что отличает его тосты от тостов готового проекта: там формулировки прежние.
      const step: IntakeSummary['step'] = { bucket, away, ...(allInOne ? { allInOne } : {}) };
      const adjusted: IntakeSummary = { ...(moved ? { ...counted, moved } : counted), step };
      // The wizard may die during commitIntake's own awaits (mint HTTP) — past the pre-commit gate.
      // Its vcr dies with it: opening the refusal window through a destroyed ViewContainerRef throws.
      this.reportOutcome(vcr, adjusted, !abandoned());
      return { committed: true, summary: adjusted };
    } finally {
      doneAsking();
      limitHold.released = true;
      for (const [key, slot] of claimed) if (held.get(key) === slot) held.delete(key);
      if (held.size === 0) this.wizardReservations.delete(projectId ?? PENDING_PROJECT_KEY);
    }
  }

  /**
   * Which conflicts of a portrait-step drop are a MOVE rather than a replacement: the name already
   * lives on the OTHER portrait step, so uploading it here again would only duplicate a photo the person
   * can already see — a landed row moves by its tags, one still uploading is retargeted (the coordinator
   * splits them, s1-ac10). `targetBucket` is filled by prepareIntake (a row
   * beyond the hydrated grid is asked for there) — an unresolved one stays a replacement, as today.
   * A group frame displays on «Все остальные фото» whatever bucket it lives in, so it never moves.
   */
  private movableConflicts(
    projectId: string | null,
    conflicts: readonly IntakeConflict[],
    bucket: UploadBucket,
  ): readonly IntakeConflict[] {
    if (projectId === null || !isPortraitStep(bucket)) return [];
    const onTheOtherStep = conflicts.filter(
      conflict =>
        conflict.existingId !== null &&
        conflict.targetBucket !== null &&
        isPortraitStep(conflict.targetBucket) &&
        conflict.targetBucket !== bucket,
    );
    // The verdicts are only worth reading once something could actually move.
    if (onTheOtherStep.length === 0) return [];
    const groupIds = groupFrameIds(this.coordinator.jobsForProject(projectId)());
    return onTheOtherStep.filter(conflict => conflict.existingId !== null && !groupIds.has(conflict.existingId));
  }

  /** Превью замены: сам брошенный файл и раздел, в котором фотограф видит его двойника. Раздел тот же,
   *  что примет замену (`targetBucket ?? bucket` — так её и заводит коммит), но групповой кадр показывается
   *  во «Всех остальных фото», в каком бы разделе он ни лежал. Из набора уходят переносы (их файлы никуда
   *  не грузятся) и имена, которые держит другой приостановленный дроп: у них ещё нет ряда в проекте. */
  private replacePreviews(
    projectId: string | null,
    conflicts: readonly IntakeConflict[],
    movable: readonly IntakeConflict[],
    bucket: UploadBucket,
  ): readonly PreviewFile[] {
    const moved = new Set(movable);
    const placed = conflicts.filter(conflict => !moved.has(conflict));
    if (projectId === null || placed.length === 0) return [];
    const groupIds = groupFrameIds(this.coordinator.jobsForProject(projectId)());
    return placed.map(conflict => ({
      file: conflict.file,
      bucket: conflict.existingId !== null && groupIds.has(conflict.existingId) ? 'other' : (conflict.targetBucket ?? bucket),
    }));
  }

  /** Сколько из набора замены лежит НЕ на том шаге, куда бросили — это окно говорит вслух, и это же
   *  решает, дождётся ли фотограф отдельного окна с результатом. */
  private awayOf(previews: readonly PreviewFile[], bucket: UploadBucket): number {
    return previews.filter(preview => preview.bucket !== bucket).length;
  }

  /** Move the tags, then let the persons follow. Best-effort on both halves: a failed move leaves every
   *  file where it was and says so, and a role that did not catch up still rides the server's own
   *  person-changed event — the photos are already tagged right either way. Files still uploading come
   *  back as a retarget, not a tag move: they count in the same line, but they have no server row for
   *  the role tail to follow and no separate rewrite to send. */
  private async commitMove(
    projectId: string,
    conflicts: readonly IntakeConflict[],
    to: PortraitStep,
    rewrite: boolean,
    abandoned: () => boolean,
  ): Promise<{ readonly moved: IntakeSummary['moved']; readonly rewrites: readonly IntakeConflict[] }> {
    let result: { readonly moved: readonly string[]; readonly landed: readonly string[] };
    try {
      result = await this.coordinator.moveConflicts(projectId, conflicts, otherStep(to), to, { rewrite });
    } catch {
      // The toast host is app-level: a move that fails after the photographer left the wizard would
      // pop this error over the cabinet, about a drop they can no longer see.
      if (!abandoned()) this.toast.error(`Не удалось перенести ${conflicts.length} фото — попробуйте ещё раз`);
      return { moved: undefined, rewrites: [] };
    }

    if (result.moved.length === 0) return { moved: undefined, rewrites: [] };
    // Fire-and-forget on purpose: the role tail reads every moved file's faces and then writes its
    // persons one after another, and the drop's FRESH files enqueue only after this method returns.
    // Awaiting it held a mixed drop's uploads — no tile, no toast — for as long as those writes took;
    // the tags, the thing the move promised, have already landed. A rejection is the role's own
    // business (the server's person-changed event still settles it), never this drop's failure.
    // A retargeted upload is not in this list: its role is decided by the tags it lands with.
    if (result.landed.length > 0)
      void this.injector
        .get(ProjectsStore)
        .followPortraitMove(projectId, result.landed, to, abandoned)
        .catch(() => undefined);
    const landed = new Set(result.landed);
    return {
      moved: { count: result.moved.length, to },
      rewrites: rewrite ? conflicts.filter(conflict => conflict.existingId !== null && landed.has(conflict.existingId)) : [],
    };
  }

  private heldNames(projectId: string | null): Map<string, ReservationSlot> {
    const projectKey = projectId ?? PENDING_PROJECT_KEY;
    let names = this.wizardReservations.get(projectKey);
    if (!names) {
      names = new Map();
      this.wizardReservations.set(projectKey, names);
    }

    return names;
  }

  /** Claim every free name for this intake; return the names currently held by ANOTHER paused intake.
   *  Recomputed after each await — a holder that committed or cancelled frees its names mid-pause. */
  private claimNames(
    held: Map<string, ReservationSlot>,
    claimed: Map<string, ReservationSlot>,
    files: readonly File[],
    keyOf: (file: File) => string = file => normalizedNameKey(file.name),
  ): Set<string> {
    const stolen = new Set<string>();
    for (const file of files) {
      const key = keyOf(file);
      if (claimed.has(key)) continue;
      if (held.has(key)) {
        stolen.add(key);
        continue;
      }

      const mine: ReservationSlot = { file, size: null };
      held.set(key, mine);
      claimed.set(key, mine);
    }

    return stolen;
  }

  private withoutKeys(
    plan: IntakePlan,
    keys: ReadonlySet<string>,
    keyOf: (file: File) => string = file => normalizedNameKey(file.name),
  ): IntakePlan {
    if (keys.size === 0) return plan;
    const keep = (file: File): boolean => !keys.has(keyOf(file));
    return {
      ...plan,
      candidates: plan.candidates.filter(keep),
      conflicts: plan.conflicts.filter(conflict => keep(conflict.file)),
      ambiguous: plan.ambiguous.filter(entry => keep(entry.file)),
      unverified: plan.unverified.filter(keep),
    };
  }

  /** Commit through the claim slots: a later drop may have swapped a newer same-name file in. Its
   *  dimensions travel with it — this plan measured the file it was about to upload, not the newcomer. */
  private throughSlots(
    plan: IntakePlan,
    claimed: Map<string, ReservationSlot>,
    keyOf: (file: File) => string = file => normalizedNameKey(file.name),
  ): IntakePlan {
    const swap = (file: File): File => claimed.get(keyOf(file))?.file ?? file;
    const sizes = new Map(plan.sizes);
    for (const slot of claimed.values()) if (slot.size) sizes.set(slot.file, slot.size);
    return {
      ...plan,
      candidates: plan.candidates.map(swap),
      conflicts: plan.conflicts.map(conflict => ({ ...conflict, file: swap(conflict.file) })),
      sizes,
    };
  }

  /** The paths through the same slots: a reservation is keyed by the PATH, so a swapped-in file stands in
   *  the very same folder. Without its own entry it leaves the manifest and never reaches that person. */
  private pathsThroughSlots(
    claimed: Map<string, ReservationSlot>,
    keyOf: (file: File) => string,
    pathByFile?: ReadonlyMap<File, string>,
  ): ReadonlyMap<File, string> | undefined {
    if (!pathByFile) return undefined;
    const paths = new Map(pathByFile);
    for (const [file, path] of pathByFile) {
      const swapped = claimed.get(keyOf(file))?.file;
      if (swapped && swapped !== file) paths.set(swapped, path);
    }

    return paths;
  }

  private async askReplaceConfirm(
    vcr: ViewContainerRef,
    context: ReplaceConfirmDialogContext,
  ): Promise<{ readonly confirmed: boolean; readonly retouch?: boolean; readonly newOnly?: boolean }> {
    const ref = this.dialogs.open<ReplaceConfirmDialogContext, ReplaceConfirmResult | undefined>(
      ReplaceConfirmDialogComponent,
      vcr,
      context,
    );
    const result = await firstValueFrom(ref.closed$.pipe(take(1)));
    return typeof result === 'object' ? result : { confirmed: result === true };
  }

  private askAmbiguousReplace(
    vcr: ViewContainerRef,
    context: AmbiguousReplaceDialogContext,
  ): Promise<AmbiguousReplaceDecision | undefined> {
    const ref = this.dialogs.open<AmbiguousReplaceDialogContext, AmbiguousReplaceDecision | undefined>(
      AmbiguousReplaceDialogComponent,
      vcr,
      context,
    );
    return firstValueFrom(ref.closed$.pipe(take(1)));
  }

  private askWizardReplace(
    vcr: ViewContainerRef,
    context: ReplaceConfirmDialogContext,
  ): Promise<WizardReplaceDecision | undefined> {
    const ref = this.dialogs.open<ReplaceConfirmDialogContext, WizardReplaceDecision | undefined>(
      ReplaceConfirmDialogComponent,
      vcr,
      context,
    );
    return firstValueFrom(ref.closed$.pipe(take(1)));
  }

  private askMove(vcr: ViewContainerRef, context: ReplaceConfirmDialogContext): Promise<MoveDecision | undefined> {
    const ref = this.dialogs.open<ReplaceConfirmDialogContext, MoveDecision | undefined>(
      ReplaceConfirmDialogComponent,
      vcr,
      context,
    );
    return firstValueFrom(ref.closed$.pipe(take(1)));
  }

  private askCheckFailed(
    vcr: ViewContainerRef,
    context?: IntakeCheckFailedDialogContext,
  ): Promise<IntakeCheckFailedDecision | undefined> {
    const ref = this.dialogs.open<IntakeCheckFailedDialogContext, IntakeCheckFailedDecision | undefined>(
      IntakeCheckFailedDialogComponent,
      vcr,
      context,
    );
    return firstValueFrom(ref.closed$.pipe(take(1)));
  }
}
